// THE TIMINGS A JS ANIMATION NEEDS, taken from the tokens rather than beside them.
//
// # Why this exists at all
//
// The animation vocabulary here was CSS transitions driven by Base UI's
// `data-starting-style` / `data-ending-style`, with every duration, easing and travel
// declared in `styles/tokens.css`. That is a good system and it is not being thrown away —
// what it cannot do is compose. A transition animates an element between two declared
// states; it cannot be interrupted with a spring, cannot hand velocity to a gesture, and
// cannot be orchestrated across several elements with one timeline.
//
// `motion` is the industrial answer to that, and its argument is not "smoother": it drives
// the Web Animations API, so a transform or opacity animation is handed to the compositor
// exactly as a CSS transition is, with the sequencing kept in code where it can be reasoned
// about.
//
// # THE TOKENS STAY THE SOURCE, and that is what this file is for
//
// motion wants NUMBERS — milliseconds and a cubic-bezier — and the tokens are CSS custom
// properties. The obvious move is to duplicate them in TypeScript, and it is the wrong one:
// two sources of one timing drift, and `check-tokens.mjs` gates the CSS half while nothing
// would gate the copy.
//
// So the values are READ from the document at the moment an animation starts. That costs one
// `getComputedStyle` per animation, which is a read of already-computed style and not a
// layout, and it buys two things worth more than the cost:
//
//   * `prefers-reduced-motion` KEEPS WORKING WITH NO CODE. `tokens.css` re-points every
//     `--motion-duration-*` to 1ms and every `--motion-travel-*` to 0 inside the media
//     query, so a reader who asked for less motion gets a 1ms animation here without a
//     single branch — which is the property `explainer.test.tsx` asserts by name, that this
//     product branches on `prefers-reduced-motion` NOWHERE.
//   * A designer moving a token moves the JS animation too, in the same edit.
//
// # WHERE A TOKEN DOES NOT RESOLVE, THE ANSWER IS ZERO — AND THAT IS THE DESIGN
//
// The obvious fallback is the number the token declares today: 200ms, 2.5rem, written here
// beside the lookup so a test gets "a sane animation" instead of `NaN`. **That is exactly
// the duplication this file exists to prevent**, one line below the paragraph arguing
// against it, and it goes stale the first time a designer retunes the ramp.
//
// So an unresolvable token animates for NO time and travels NO distance. Two things make
// that the right answer rather than a shrug:
//
//   * **An environment that cannot resolve the tokens cannot render the animation either.**
//     jsdom has `getComputedStyle` and no stylesheet behind it, so every custom property
//     comes back empty — and there is no compositor, no paint and nobody watching. An
//     instant transition is what that environment should have; a 200ms one merely leaves
//     elements mid-fade for whatever a test happens to await, which is how a pane ends up
//     invisible to `toBeVisible()` for reasons that have nothing to do with the product.
//   * **In a browser it fails LOUDLY and in the safe direction.** A misspelt token is a
//     control that appears instantly instead of one that animates for an invented duration
//     nobody chose — visible on the first press, and never a timing that looks deliberate.
//     `motion.test.ts` holds the names, so this is the second line of defence.
//
// It degrades to the same behaviour `prefers-reduced-motion` asks for, which is the one
// direction a fallback here is allowed to fail in.

/** What a token is worth, in milliseconds. Zero where nothing resolves it — see above. */
export function durationOf(token: string): number {
  const raw = readToken(token);
  if (!raw) {
    return 0;
  }
  // `120ms` or `0.12s`. Both are legal CSS and the tokens use the first; the second is
  // parsed anyway because a token somebody re-declares in seconds should not silently
  // animate for a hundred and twenty seconds.
  const ms = raw.endsWith("ms") ? Number.parseFloat(raw) : Number.parseFloat(raw) * 1000;
  return Number.isFinite(ms) ? ms : 0;
}

/** A `--motion-travel-*` token in pixels. Zero where nothing resolves it — see above. */
export function travelOf(token: string): number {
  const raw = readToken(token);
  if (!raw) {
    return 0;
  }
  // `rem` because that is what the travel tokens are declared in, and a pane that moved a
  // fixed number of pixels would move a different FRACTION of itself at every root size.
  const root = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const value = Number.parseFloat(raw);
  if (!Number.isFinite(value)) {
    return 0;
  }
  return raw.includes("rem") ? value * root : value;
}

/**
 * A `--motion-ease-*` token as motion's easing.
 *
 * The tokens are `cubic-bezier(a, b, c, d)`; motion takes the four numbers. Anything it
 * cannot parse comes back as the fallback rather than as a broken curve, because a
 * malformed easing in motion is not an error — it is a linear animation nobody notices.
 */
export function easeOf(token: string, fallback: [number, number, number, number]) {
  const raw = readToken(token);
  const numbers = raw.match(/-?\d*\.?\d+/g);
  if (numbers?.length !== 4) {
    return fallback;
  }
  const parsed = numbers.map(Number);
  return parsed.every(Number.isFinite)
    ? ([parsed[0], parsed[1], parsed[2], parsed[3]] as [number, number, number, number])
    : fallback;
}

/**
 * READ FROM THE ROOT, and per call rather than once.
 *
 * Once would be cheaper and would freeze the answer: a reader who turns reduced motion on
 * mid-session, or a theme that re-points a token, would go on getting the value that was
 * true when the module loaded. This is the same reason `today()` is a call and not a
 * constant.
 */
function readToken(token: string): string {
  if (typeof document === "undefined") {
    return "";
  }
  return getComputedStyle(document.documentElement).getPropertyValue(token).trim();
}
